/**
 * 結合（グループ）の算出。
 *
 * **グループは保持状態ではなく導出量である**（ADR-010）。`placement` と `target` から毎回
 * 一から算出し、前回の結果を一切引き継がない。引き継ぐと、押しのけで分解されたはずの
 * グループが残って不変条件 I-2 が破れる。Union-Find はこの再計算に使う道具であって、
 * 盤面をまたいで持ち越す状態ではない。
 *
 * 根拠: spec/04a-puzzle-core.md §2、02-requirements.md FR-009。
 */

import { adjacentCellPairs, col, row } from './grid';
import { at } from './internal';
import type { CellIndex, PieceId, Placement, PositionOf, TargetLayout } from './types';

/**
 * グループの算出結果。
 *
 * ラベルは「構成ピースの最小 ID が小さい順」に 0 から振る。同じ盤面からは常に同じラベルが
 * 得られるため、結果をそのまま比較・スナップショットできる。
 */
export interface Groups {
  /** グループ数。 */
  readonly count: number;
  /** `labelOf[piece]` = そのピースが属するグループのラベル。 */
  readonly labelOf: readonly number[];
  /** `members[label]` = そのグループの構成ピース（ピース ID の昇順）。 */
  readonly members: readonly (readonly PieceId[])[];
}

/**
 * 盤面と目標配置から連結成分を算出する。
 *
 * 隣接セル対を各 1 回だけ走査し（`adjacentCellPairs`）、結合条件を満たす対を union する。
 * 計算量は `O(N·α(N))`、実質 `O(N)`。
 *
 * @param placement 各セルにいるピース
 * @param target 各ピースの正解セル（第 1 段階は `τ`、第 2 段階は `id`）
 * @param C 列数
 */
export function computeGroups(placement: Placement, target: TargetLayout, C: number): Groups {
  const N = placement.length;
  const R = N / C;
  const uf = new UnionFind(N);

  for (const [a, b] of adjacentCellPairs(C, R)) {
    const p = at(placement, a);
    const q = at(placement, b);
    if (isJoined(a, b, p, q, target, C)) uf.union(p, q);
  }

  // ラベルはピース ID の昇順に振る。Union-Find の根の番号に依存させると、union の
  // 順序（= 実装の都合）が結果に漏れ出す。
  const labelOf = new Array<number>(N).fill(-1);
  const members: PieceId[][] = [];
  for (let piece = 0; piece < N; piece++) {
    const root = uf.find(piece);
    if (at(labelOf, root) === -1) {
      labelOf[root] = members.length;
      members.push([]);
    }
  }
  for (let piece = 0; piece < N; piece++) {
    const label = at(labelOf, uf.find(piece));
    labelOf[piece] = label;
    at(members, label).push(piece);
  }

  return { count: members.length, labelOf, members };
}

/**
 * ピース `p` が属するグループの構成ピースを返す。
 *
 * 返すのは複製であり、これを書き換えても `groups` は変わらない（グループは導出量である
 * という前提を、呼び出し側の事故から守る）。
 */
export function groupOf(groups: Groups, p: PieceId): PieceId[] {
  return [...at(groups.members, at(groups.labelOf, p))];
}

/**
 * ピース集合が盤面上で占めるセル集合（footprint）。
 *
 * グループに対して呼べば、その footprint は連結なポリオミノになる（不変条件 I-3）。
 * 返り値はセル番号の昇順。
 */
export function footprint(pieces: PieceId[], positionOf: PositionOf): CellIndex[] {
  const cells = pieces.map((piece) => at(positionOf, piece));
  return cells.sort((x, y) => x - y);
}

/**
 * セル `a` のピース `p` と、隣接するセル `b` のピース `q` が結合条件を満たすか。
 *
 * 盤面上の相対位置が、目標配置における相対位置と一致することを要求する（`04a` §2.1）。
 */
function isJoined(
  a: CellIndex,
  b: CellIndex,
  p: PieceId,
  q: PieceId,
  target: TargetLayout,
  C: number,
): boolean {
  const tp = at(target, p);
  const tq = at(target, q);
  return (
    col(b, C) - col(a, C) === col(tq, C) - col(tp, C) &&
    row(b, C) - row(a, C) === row(tq, C) - row(tp, C)
  );
}

/** 経路圧縮とサイズによる併合を行う Union-Find。呼び出しごとに作り直す。 */
class UnionFind {
  private readonly parent: Int32Array;
  private readonly size: Int32Array;

  constructor(n: number) {
    this.parent = new Int32Array(n);
    this.size = new Int32Array(n).fill(1);
    for (let i = 0; i < n; i++) this.parent[i] = i;
  }

  find(x: number): number {
    let root = x;
    while (at(this.parent, root) !== root) root = at(this.parent, root);
    // 経路圧縮。次回以降の find を実質定数時間にする。
    let cursor = x;
    while (at(this.parent, cursor) !== root) {
      const next = at(this.parent, cursor);
      this.parent[cursor] = root;
      cursor = next;
    }
    return root;
  }

  union(x: number, y: number): void {
    let rx = this.find(x);
    let ry = this.find(y);
    if (rx === ry) return;
    if (at(this.size, rx) < at(this.size, ry)) [rx, ry] = [ry, rx];
    this.parent[ry] = rx;
    this.size[rx] = at(this.size, rx) + at(this.size, ry);
  }
}
